// Phase 11c — operator-only admin surface for caregiver payouts.
//
// 6 endpoints under /admin/payouts (mirroring the 10c /admin/invoices
// surface for facility AR):
//   - GET    /admin/payouts                — list (status / caregiver / cycle filters)
//   - GET    /admin/payouts/:id            — detail (incl. lines + events + caregiver context)
//   - POST   /admin/payouts/:id/issue      — DRAFT → ISSUED
//   - POST   /admin/payouts/:id/void       — non-VOIDED → VOIDED with reason
//   - POST   /admin/payouts/:id/regenerate — DRAFT-only line rebuild
//   - POST   /admin/payouts/force-cycle-close — single-caregiver re-close
//
// Conventions (10c precedent):
//   - Money fields use INTEGER cents end-to-end (10a-1).
//   - Dates use 'YYYY-MM-DD' for date-only columns + ISO-8601 for timestamps.
//   - Every mutation request carries `reason` (≥10 chars) where applicable.
import { z } from 'zod';
import { PayoutFrequencySchema, PayoutMechanicSchema, PayoutStatusSchema } from './enums.schema.js';
// ===== shared pieces =====
const IsoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD');
const ReasonSchema = z
    .string()
    .min(10, 'Reason must be at least 10 characters — document why')
    .max(500);
// ===== list =====
export const AdminPayoutListQuerySchema = z.object({
    status: PayoutStatusSchema.optional(),
    // Filter by the caregiver's payout cadence (WEEKLY / BIWEEKLY). Server-side so
    // the /admin/payouts overview page filters via normal pagination instead of
    // fetching an oversized page to filter client-side.
    payoutFrequency: PayoutFrequencySchema.optional(),
    // The dashboard "Failed payouts" view: stale DRAFTs (> 7 days) + ISSUED payouts
    // with a prior EFT rejection. Server-side + shares the metric's selector so the
    // card count and this list match exactly. Supersedes `status` when true.
    // (Not `z.coerce.boolean()` — Boolean('false') is truthy; parse the string.)
    needsAttention: z
        .union([z.boolean(), z.enum(['true', 'false']).transform((v) => v === 'true')])
        .optional(),
    caregiverId: z.string().uuid().optional(),
    cycleStartDate: IsoDateSchema.optional(),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(20),
});
export const AdminPayoutListItemSchema = z.object({
    id: z.string().uuid(),
    payoutNumber: z.string().nullable(),
    caregiverId: z.string().uuid(),
    caregiverName: z.string(),
    caregiverEmail: z.string().nullable(),
    status: PayoutStatusSchema,
    subtotalCents: z.number().int(),
    hstCents: z.number().int(),
    totalCents: z.number().int(),
    cycleStartDate: IsoDateSchema,
    cycleEndDate: IsoDateSchema,
    issuedAt: z.string().nullable(),
    holdUntil: z.string().nullable(),
    paidAt: z.string().nullable(),
    voidedAt: z.string().nullable(),
    payoutFrequency: PayoutFrequencySchema,
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const AdminPayoutListResponseSchema = z.object({
    items: z.array(AdminPayoutListItemSchema),
    totalCount: z.number().int().min(0),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
// ===== detail =====
export const AdminPayoutLineSchema = z.object({
    id: z.string().uuid(),
    shiftId: z.string().uuid().nullable(),
    lineType: z.enum(['SHIFT_PAY', 'STAT_HOLIDAY_PAY', 'CANCELLATION_PAY', 'BONUS', 'MANUAL_ADJUSTMENT']),
    description: z.string(),
    quantityHours: z.string(),
    unitPayRate: z.string(),
    subtotalCents: z.number().int(),
    appliedPolicyId: z.string().uuid().nullable(),
});
export const AdminPayoutEventSchema = z.object({
    id: z.string().uuid(),
    eventType: z.enum([
        'EFT_DISPATCHED',
        'EFT_SETTLED',
        'EFT_REJECTED',
        'INTERAC_SENT',
        'CHEQUE_MAILED',
        'MANUAL_CREDIT',
        'REVERSAL',
    ]),
    amountCents: z.number().int(),
    externalRef: z.string().nullable(),
    recordedAt: z.string(),
    recordedByUserId: z.string().uuid().nullable(),
});
export const AdminPayoutDetailResponseSchema = AdminPayoutListItemSchema.extend({
    mechanic: PayoutMechanicSchema.nullable(),
    externalRef: z.string().nullable(),
    voidReason: z.string().nullable(),
    statementPdfKey: z.string().nullable(),
    hstRegistrantAtIssue: z.boolean(),
    hstNumberAtIssue: z.string().nullable(),
    lines: z.array(AdminPayoutLineSchema),
    events: z.array(AdminPayoutEventSchema),
});
// ===== mutations =====
export const AdminPayoutVoidRequestSchema = z.object({
    reason: ReasonSchema,
});
export const AdminPayoutRegenerateRequestSchema = z.object({
    reason: ReasonSchema,
});
export const AdminPayoutForceCycleCloseRequestSchema = z.object({
    caregiverId: z.string().uuid(),
    cycleStartDate: IsoDateSchema, // must be a Monday — service validates
    reason: ReasonSchema,
});
export const AdminPayoutForceCycleCloseResponseSchema = z.object({
    caregiverId: z.string().uuid(),
    cycleStartDate: IsoDateSchema,
    cycleEndDate: IsoDateSchema,
    payoutId: z.string().uuid().nullable(),
    skipped: z.enum(['NO_SHIFTS', 'ALREADY_HAS_LIVE_PAYOUT']).nullable(),
    lineCount: z.number().int().min(0),
    totalCents: z.number().int().min(0),
});
// ===== Phase 11e — record-payment + record-eft-dispatch + record-eft-rejection =====
/** Per 11e-3 — POST /admin/payouts/:id/record-payment body shape.
 *  Success path: ISSUED → PAID. Mechanic enum is bank-agnostic per 11a-7. */
export const AdminPayoutRecordPaymentRequestSchema = z.object({
    mechanic: PayoutMechanicSchema,
    externalRef: z.string().min(1).max(200).optional(),
    /** Optional backfill — when the bank settled earlier than the operator records.
     *  Defaults to now if omitted. */
    settledAt: z.string().datetime().optional(),
    notes: z.string().max(2000).optional(),
});
/** Per 11e-7 — POST /admin/payouts/:id/record-eft-dispatch body shape.
 *  Operator-opt-in dispatch audit; status stays ISSUED. */
export const AdminPayoutRecordEftDispatchRequestSchema = z.object({
    externalRef: z.string().min(1).max(200),
    /** Optional backfill — when the operator actually dispatched the EFT batch
     *  to the bank. Defaults to now if omitted. */
    dispatchedAt: z.string().datetime().optional(),
    notes: z.string().max(2000).optional(),
});
/** Per 11e-4 — POST /admin/payouts/:id/record-eft-rejection body shape.
 *  Bank rejected the EFT; payout status stays ISSUED so a fallback mechanic
 *  can be recorded later via record-payment (per 11e-5 auto-detect). */
export const AdminPayoutRecordEftRejectionRequestSchema = z.object({
    rejectionReason: ReasonSchema,
    externalRef: z.string().min(1).max(200).optional(),
});
//# sourceMappingURL=payout-admin.schema.js.map